/**
 * Trip review API calls: the post-trip review, multi-user trip feedback,
 * and accommodation reviews.
 */
import { invalidateDashboardCache } from '../../hooks/useHomeDashboard';
import { apiRequest } from './request';
import { transformTripData } from './transform';

/**
 * Submit post-trip review
 * @param {string} tripId - Trip ID
 * @param {object} reviewData - Review details
 * @returns {object} Result
 */
export function submitTripReview(tripId, reviewData) {
  return apiRequest(`/api/trips/${tripId}/review`, {
    method: 'POST',
    body: reviewData,
    errorLabel: 'submitting trip review',
    transform: (result) => {
      // Transform the returned trip from snake_case to camelCase
      if (result.success && result.trip) {
        result.trip = transformTripData(result.trip);
      }
      // Drop the home-dashboard cache so the trip leaves "Coming Up" immediately
      // (otherwise it lingers until the 60s TTL expires).
      if (result.success) invalidateDashboardCache();
      return result;
    }
  });
}

/**
 * Get trips that need post-trip reviews
 * @returns {array} Array of trips needing review
 */
export function getTripsNeedingReview() {
  return apiRequest('/api/trips/needing-review', {
    errorLabel: 'getting trips needing review',
    transform: (data) => (data.trips || []).map(trip => transformTripData(trip)),
    onError: () => []
  });
}

/**
 * Submit trip feedback (new multi-user method)
 * @param {string} tripId - Trip ID
 * @param {object} feedbackData - Feedback details
 * @returns {object} Result
 */
export function submitTripFeedback(tripId, feedbackData) {
  return apiRequest(`/api/trips/${tripId}/feedback`, {
    method: 'POST',
    body: feedbackData,
    errorLabel: 'submitting trip feedback',
    transform: (result) => {
      // Drop the home-dashboard cache so the trip leaves "Coming Up" immediately.
      if (result.success) invalidateDashboardCache();
      return result;
    }
  });
}

/**
 * Get current user's feedback for a trip
 * @param {string} tripId - Trip ID
 * @returns {object} Result with feedback data
 */
export function getMyTripFeedback(tripId) {
  return apiRequest(`/api/trips/${tripId}/feedback/me`, {
    errorLabel: 'getting trip feedback'
  });
}

/**
 * Get all traveler feedback for a trip
 * @param {string} tripId - Trip ID
 * @returns {object} Result with all feedback
 */
export function getTripFeedback(tripId) {
  return apiRequest(`/api/trips/${tripId}/feedback`, {
    errorLabel: 'getting trip feedback'
  });
}

/**
 * Submit a batch of accommodation reviews. Best-effort — each item
 * succeeds or fails independently; check `results[i].success` per item.
 * @param {Array<object>} reviews - [{ accommodationId, ...fields }, ...]
 * @returns {object} { success, results: [...] }
 */
export function submitAccommodationReviewBatch(reviews) {
  return apiRequest('/api/accommodation-reviews/batch', {
    method: 'POST',
    body: { reviews },
    errorLabel: 'submitting accommodation review batch'
  });
}

/**
 * Get all accommodation reviews for a trip (visibility-clamped server-side).
 * Used by TripReviewForm on mount to hydrate the form with prior submissions.
 * @param {string} tripId
 * @returns {object} { success, reviews: [...] }
 */
export function getAccommodationReviewsForTrip(tripId) {
  return apiRequest(`/api/accommodation-reviews/by-trip/${tripId}`, {
    errorLabel: 'getting accommodation reviews',
    onError: (error) => ({ success: false, error: error.message, reviews: [] })
  });
}

/**
 * The day ids the viewer marked "No photos" on the Recap tab.
 * @param {string} tripId
 * @returns {Promise<number[]|null>} null when the read failed
 */
export function getNoPhotoDays(tripId) {
  return apiRequest(`/api/trips/${tripId}/recap/no-photo-days`, {
    errorLabel: 'getting no-photo days',
    transform: (data) => (data?.success ? (data.trip_day_ids || []) : null),
    onError: () => null
  });
}

/**
 * Mark (noPhotos true) or unmark a day's ids as "No photos". Only those ids
 * change on the server.
 * @returns {Promise<{ success: boolean, trip_day_ids?: number[], error?: string }>}
 */
export function setNoPhotoDays(tripId, tripDayIds, noPhotos) {
  return apiRequest(`/api/trips/${tripId}/recap/no-photo-days`, {
    method: 'PUT',
    body: { trip_day_ids: tripDayIds, no_photos: noPhotos },
    errorLabel: 'saving no-photo days'
  });
}

/**
 * Ask the server to derive a traveler_type suggestion from trip composition.
 * Server uses trip start_date as the age reference. Returns null if it can't
 * tell — client falls back to manual selection.
 * @param {string} tripId
 * @returns {string|null}
 */
export function getDerivedTravelerType(tripId) {
  return apiRequest(`/api/accommodation-reviews/derived-traveler-type/${tripId}`, {
    errorLabel: 'getting derived traveler type',
    transform: (data) => data?.travelerType ?? null,
    onError: () => null
  });
}

/**
 * Soft-delete the user's own accommodation review. The server only allows
 * the review's owner to delete (route returns 403 otherwise).
 * @param {number} reviewId
 * @returns {object} { success, error? }
 */
export function deleteAccommodationReview(reviewId) {
  return apiRequest(`/api/accommodation-reviews/${reviewId}`, {
    method: 'DELETE',
    errorLabel: 'deleting accommodation review'
  });
}
